/**
 * Rogue Alloy YouTube click-to-load facades (design: "Social" package, 26
 * September 2026).
 *
 * Framework-free ES module, loaded with `defer` from sections/rogue-youtube.liquid,
 * only when that section actually rendered a video. Every facade is a plain
 * link to the video on youtube.com by default (no JS: the link just opens
 * YouTube, exactly as any other outbound link would), so this script's only
 * job is to intercept that click and swap the facade for a
 * youtube-nocookie.com iframe instead - nothing from YouTube's own domain
 * loads, and no cookie is set, until a visitor actually asks for a video.
 *
 * Unit tested by tests/rogue-youtube.test.js (buildEmbedSrc only; the DOM
 * swap needs a real click and a real iframe, which is what the browser
 * checks are for).
 */

/**
 * The privacy-enhanced embed URL for one video, with autoplay so the click
 * that revealed the iframe is also the click that starts it.
 * @param {string} videoId
 * @returns {string}
 */
export function buildEmbedSrc(videoId) {
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0`;
}

/**
 * Replaces one facade `<a>` with a live iframe. Never called twice for the
 * same facade: the listener is removed by the replacement itself (the
 * original element leaves the DOM).
 * @param {HTMLAnchorElement} facade
 */
function loadVideo(facade) {
  const videoId = facade.getAttribute('data-rogue-youtube-id');
  if (!videoId) return;

  const title = facade.getAttribute('data-rogue-youtube-title') || 'Rogue Alloy on YouTube';

  const iframe = document.createElement('iframe');
  iframe.className = 'rogue-youtube__frame';
  iframe.src = buildEmbedSrc(videoId);
  iframe.title = title;
  iframe.loading = 'lazy';
  iframe.allow = 'autoplay; encrypted-media; picture-in-picture; web-share';
  iframe.allowFullscreen = true;
  iframe.setAttribute('frameborder', '0');

  facade.replaceWith(iframe);
}

/**
 * Wires up every `[data-rogue-youtube]` facade under `root`: a click is
 * intercepted (so the plain youtube.com link never navigates once JS is
 * running) and swapped for a live embed instead.
 * @param {Document|Element} [root]
 */
export function initRogueYoutube(root = document) {
  if (typeof root.querySelectorAll !== 'function') return;

  const facades = root.querySelectorAll('[data-rogue-youtube]');
  facades.forEach((facade) => {
    facade.addEventListener('click', (event) => {
      event.preventDefault();
      loadVideo(facade);
    });
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initRogueYoutube());
  } else {
    initRogueYoutube();
  }
}
